import React from 'react';
import PropTypes from 'prop-types';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import ChannelKey from '@avtechsoftware/ra-javascript-utilities/src/classes/ChannelKey';
import { OperatorTypeDetails } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import BooleanChannel from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BooleanChannel/BooleanChannel';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import RaaExtremeTempFaultChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/RaaExtremeTempFaultChannel';
import ExtremeTemperatureFaultChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/ExtremeTemperatureFaultChannel/ExtremeTemperatureFaultChannel';
import IntegerAsBooleanChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/IntegerAsBooleanChannel/IntegerAsBooleanChannel';
import styles from './AlertListTableRow.module.css';
import TriggeredAlertNameIcon from './TriggeredAlertNameIcon/TriggeredAlertNameIcon';
import listAlertPropType from '../../../../../utility/PropTypes/listAlertPropType';
import AlertRowLastReadingBadge from './AlertRowLastReadingBadge/AlertRowLastReadingBadge';
import ChannelValueWithUnitsFromChannelKeyObject
  from '../../../../common/ChannelValueWithUnitsFromChannelKeyObject/ChannelValueWithUnitsFromChannelKeyObject';
import { selectAlertListColumnVisibility } from '../../../../../redux/slices/alertListSlice/alertListSlice';
import AlertListRowOverlay from '../../../AlertListRowOverlay/AlertListRowOverlay';
import { selectSessionInformation } from '../../../../../redux/slices/sessionSlice';
import getChannelValueWithUnitsFromChannelKeyObjectString
  from '../../../../common/ChannelValueWithUnitsFromChannelKeyObject/getChannelValueWithUnitsFromChannelKeyObjectString';
import { selectAllTagsByTagIdList } from '../../../../../redux/slices/tagListSlice/tagListSlice';
import { RaaRoles } from '../../../../../utility/SecurityRules';
import HasRolePrivileges from '../../../../highorder/HasRolePrivileges';
import { getAlertSource, getMaxComponentLabel } from '../../../../../utility/alertDisplayUtils';
import { selectByUuidOrMacAddress, selectSensorMap } from '../../../../../redux/slices/deviceList/deviceListSlice';

/* istanbul ignore next */
function AlertListTableRow({
  alert,
  isPublicDashboard,
  index,
  hoveredIndex,
  setHoveredIndex,
  isSelected,
  handleSetSelected,
  disableQuickActions,
}) {
  const sessionInformation = useSelector(selectSessionInformation);
  const device = useSelector(
    (state) => selectByUuidOrMacAddress(state, alert.channelKey.macAddress),
  );
  const isRAMAXDevice = device && device.type === 'RAMAX';
  const sensorMap = useSelector(selectSensorMap);
  const deviceSensorList = sensorMap[alert.channelKey.macAddress] || [];

  let maxComponentLabel = '';
  if (isRAMAXDevice && deviceSensorList && deviceSensorList.length > 0) {
    maxComponentLabel = getMaxComponentLabel(deviceSensorList, alert.channelKey);
  }
  const alertListColumnVisibility = useSelector(selectAlertListColumnVisibility);
  const isAlertTriggered = alert.triggeredTimestamp !== null;
  const channelKeyObject = new ChannelKey(alert.channelKey.channelKey);
  const channel = ChannelService.getChannelGroupChannel(
    channelKeyObject.channelGroupType,
    channelKeyObject.channelIndex,
    channelKeyObject.channelType,
  );
  const {
    reverseOperator,
    symbol,
    hysteresisMultiplier,
  } = OperatorTypeDetails[alert.startOperatorTypeId];
  const startOperatorSymbol = symbol;
  const clearOperator = OperatorTypeDetails[reverseOperator];
  const clearOperatorSymbol = clearOperator.symbol;
  const tags = useSelector((state) => selectAllTagsByTagIdList(state, alert.tagIds));
  const combinedTags = tags
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((tag) => tag.name)
    .join(', ');
  const specialChannelList = [
    BooleanChannel,
    RaaExtremeTempFaultChannel,
    // TODO could be cleaned up by ensuring the same channel type is returned by both apis
    // using below class because ra-api returns channel type with ExtremeTemperatureFaultChannel,
    // but the single alert api returns RaaExtremeTempFaultChannel
    ExtremeTemperatureFaultChannel,
    IntegerAsBooleanChannel,
  ];

  const getColStyle = (label) => (alertListColumnVisibility[label] === false ? { display: 'none' } : {});
  const getHoveredStyle = () => (hoveredIndex === index ? `row-hovered avt-table-row ${styles.tableRow}` : `avt-table-row ${styles.tableRow}`);
  const history = useHistory();

  const redirectToAlert = () => {
    if (!isPublicDashboard) {
      history.push(`/s/alerts/${alert.id}`);
    }
  };

  const handleBoolAndFaultConditionDisplay = (value, isTrigger) => {
    if (channel instanceof BooleanChannel
        && !(channel instanceof ExtremeTemperatureFaultChannel)
        && !(channel instanceof RaaExtremeTempFaultChannel)) {
      return channel.getDisplayValue(value);
    }
    if (channel instanceof ExtremeTemperatureFaultChannel
        || channel instanceof RaaExtremeTempFaultChannel
        || channel instanceof IntegerAsBooleanChannel) {
      const isFaultTriggerCondition = (alert.startThreshold === 1
          && alert.startOperatorTypeId === 3);
      if (isTrigger) {
        return isFaultTriggerCondition ? 'No Fault' : 'Fault';
      }
      return isFaultTriggerCondition ? 'Fault' : 'No Fault';
    }
    return '';
  };

  return (
    <tr
      style={{ backgroundColor: isSelected ? '#9fc5f8' : '', cursor: 'pointer' }}
      className={getHoveredStyle()}
      onMouseEnter={() => setHoveredIndex(index)}
      onMouseLeave={() => setHoveredIndex(null)}
    >
      <HasRolePrivileges targetRoleList={[RaaRoles.ADMIN, RaaRoles.MANAGER]}>
        <td id="alert-checkbox-col" style={{ paddingLeft: '0.75rem', paddingRight: '1.0rem' }}>
          <span>
            <input
              type="checkbox"
              id={`${alert.description}-checkbox`}
              name={alert.description}
              checked={isSelected}
              onChange={() => handleSetSelected()}
            />
          </span>
        </td>
      </HasRolePrivileges>
      <td onClick={redirectToAlert}>
        <span className="ml-1">
          {
              isAlertTriggered ? (
                <TriggeredAlertNameIcon isAcknowledged={alert.isAcknowledged} />
              ) : (
                <span />
              )
          }
        </span>
      </td>
      {alertListColumnVisibility.Name && (
        <td
          id="alert-name-col"
          className="ellipsis width-25"
          style={getColStyle('Name')}
          title={alert.description}
          onClick={redirectToAlert}
        >
          <span className="ml-1">
            <>
              {' '}
              {alert.description}
              {' '}
            </>
          </span>
        </td>
      )}

      {alertListColumnVisibility.Source && (
      <td
        id="alert-source-col"
        style={getColStyle('Source')}
        className="ellipsis width-25"
        title={getAlertSource(alert, channel, isRAMAXDevice, maxComponentLabel)}
        onClick={redirectToAlert}
      >
        <span>{getAlertSource(alert, channel, isRAMAXDevice, maxComponentLabel)}</span>
      </td>

      )}

      {alertListColumnVisibility.Tags && (
      <td id="alert-tags-col" style={getColStyle('Tags')} className="ellipsis width-15" title={combinedTags}>
        <span>
          {combinedTags}
        </span>
      </td>
      )}

      {alertListColumnVisibility.Trigger && (
      <td
        id="alert-trigger-col"
        className="ellipsis width-15"
        style={getColStyle('Trigger')}
        title={
          specialChannelList.some((chan) => channel instanceof chan)
            ? handleBoolAndFaultConditionDisplay(alert.startThreshold, true)
            : `${startOperatorSymbol} ${getChannelValueWithUnitsFromChannelKeyObjectString(sessionInformation, channelKeyObject, alert.startThreshold, alert.customUnit)}`
        }
        onClick={redirectToAlert}
      >
        <span>
          {
            specialChannelList.some((chan) => channel instanceof chan)
              ? handleBoolAndFaultConditionDisplay(alert.startThreshold, true)
              : (
                <>
                  {' '}
                  {startOperatorSymbol}
                  {' '}
                  <ChannelValueWithUnitsFromChannelKeyObject
                    channelKey={channelKeyObject}
                    customUnits={alert.customUnit}
                    value={alert.startThreshold}
                  />
                </>
              )
        }
        </span>
      </td>
      )}

      {alertListColumnVisibility.Clear && (
      <td
        id="alert-clear-col"
        className="ellipsis width-10"
        style={getColStyle('Clear')}
        onClick={redirectToAlert}
        title={
          specialChannelList.some((chan) => channel instanceof chan)
            ? handleBoolAndFaultConditionDisplay(!alert.startThreshold, false)
            : `${startOperatorSymbol} ${getChannelValueWithUnitsFromChannelKeyObjectString(sessionInformation, channelKeyObject, (alert.startThreshold
                  + (channel.hysteresisBuffer * hysteresisMultiplier)), alert.customUnit)}`
      }
      >
        <span>
          {
           specialChannelList.some((chan) => channel instanceof chan)
             ? handleBoolAndFaultConditionDisplay(!alert.startThreshold, false)
             : (
               <>
                 {' '}
                 {clearOperatorSymbol}
                 {' '}
                 <ChannelValueWithUnitsFromChannelKeyObject
                   channelKey={channelKeyObject}
                   customUnits={alert.customUnit}
                   value={alert.startThreshold
                             + (channel.hysteresisBuffer * hysteresisMultiplier)}
                 />
               </>
             )
        }
        </span>
      </td>
      )}

      {alertListColumnVisibility.Current && (
        <td
          id="alert-current-col"
          className="nowrap width-10"
          style={getColStyle('Current')}
          onClick={redirectToAlert}
        >
          <AlertRowLastReadingBadge
            isSuspended={alert.isSuspended}
            isDeviceSuspended={alert.isDeviceSuspended}
            lastReading={alert.lastSampleValue}
            lastReadingTimestamp={alert.lastSampleTimestamp}
            isTriggered={isAlertTriggered}
            triggeredTimestamp={alert.triggeredTimestamp}
            isAcknowledged={alert.acknowledgementTimestamp !== null}
            channelKeyObj={channelKeyObject}
            customUnit={alert.customUnit}
            customScales={sessionInformation}
          />
        </td>
      )}

      {/* <td id="alert-actions-col"> */}
      {/*  /!* TODO: datum should display total # of recipients across all notif groups *!/ */}
      {/*  <ConditionalRender show={alert.alertTypeList && alert.alertTypeList.length > 0}> */}
      {/*    <span> */}
      {/*      {alert.alertTypeList !== undefined ? alert.alertTypeList.length : null} */}
      {/*      {' '} */}
      {/*    </span> */}
      {/*    <span> */}
      {/*      {alert.alertTypeList !== undefined */}
      {/*        ? alert.alertTypeList.map((alertType, index) => ( */}
      {/*          <React.Fragment key={index}> */}
      {/*            <Icon */}
      {/*              icon={alertTypeIconMap[alertType.type]} */}
      {/*              className="mr-1" */}
      {/*            /> */}
      {/*          </React.Fragment> */}
      {/*        )) */}
      {/*        : null} */}
      {/*    </span> */}
      {/*  </ConditionalRender> */}
      {/*  <ConditionalRender */}
      {/*    show={(alert.alertTypeList !== undefined && alert.alertTypeList.length === 0)} */}
      {/*  > */}
      {/*    <span>No Actions</span> */}
      {/*  </ConditionalRender> */}
      {/* </td> */}

      {!disableQuickActions && hoveredIndex === index && (
        <HasRolePrivileges targetRoleList={[RaaRoles.ADMIN, RaaRoles.MANAGER]}>
          <AlertListRowOverlay index={index} alertId={alert.id} alertName={alert.description} />
        </HasRolePrivileges>
      )}
    </tr>
  );
}

AlertListTableRow.propTypes = {
  alert: PropTypes.shape(listAlertPropType).isRequired,
  isPublicDashboard: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  hoveredIndex: PropTypes.number.isRequired,
  setHoveredIndex: PropTypes.func.isRequired,
  handleSetSelected: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  disableQuickActions: PropTypes.bool.isRequired,
};

export default AlertListTableRow;
